import dayjs from "dayjs"
import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
  CommentPlatform,
  useGetAdReactionsQuery,
  useGetCommentDashboardQuery,
  useGetCommentWordCloudQuery,
  useListAdSummariesQuery,
  useListCommentDecisionsQuery,
} from "~/ui-rtk/api/commentModerationApi"
import Button from "~/ui-rtk/components/ui/controls/Button"
import {
  DateRangePicker,
  SegmentedToggle,
  type SegmentedToggleOption,
} from "~/ui-rtk/components/ui/controls"
import { AggregationType } from "~/ui-rtk/constants/charts"
import { useMinMaxDateRange } from "~/ui-rtk/hooks/date-range"
import { DATE_FORMAT, isValidDate, usePeriod } from "~/ui-rtk/hooks/period"
import AdsTable from "./AdsTable"
import CommentDashboard from "./CommentDashboard"
import CommentWordCloud from "./CommentWordCloud"
import ModerationSettings from "./ModerationSettings"
import { AspectRows } from "./reactions"

const PLATFORM_OPTIONS: SegmentedToggleOption[] = [
  { label: "Both", value: "", title: "Facebook and Instagram" },
  { label: "Facebook", value: "FACEBOOK" },
  { label: "Instagram", value: "INSTAGRAM" },
]

function StatTile({
  label,
  value,
  hint,
}: {
  label: string
  value: number | string
  hint?: string
}) {
  return (
    <div
      className="rounded-lg border border-basic-blue bg-basic-dark-blue px-4 py-3"
      title={hint}
    >
      <div className="text-xs uppercase tracking-wide text-system-gray-500">
        {label}
      </div>
      <div className="text-3xl font-semibold text-white">{value}</div>
    </div>
  )
}

/**
 * The comment moderation report (MD-5576). One row per ad that got comments
 * on its posts in the chosen window, with how the audience reacted and the
 * AI headline; clicking an ad opens a drawer with the full read and every
 * judged comment (hide / unhide per row). Trends (daily categories, what
 * people talk about, the word cloud) fold away below. Deep-linkable with
 * `?from=` (the Slack digest and the job result point here).
 */
// Decisions are dated by the run that made them, so the window may end today.
const maxDateRestriction = dayjs().toDate()

export default function CommentModerationPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const platform = (searchParams.get("platform") || "") as CommentPlatform | ""
  const [trendsOpen, setTrendsOpen] = useState(false)

  // The window lives in the app's global period params (p_ds / p_de), like
  // every other dated page, so the house DateRangePicker drives it. A Slack
  // or job-result deep link still passes `?from=`; it seeds the default
  // start the first time the page opens without a period.
  const deepLinkFrom = searchParams.get("from")
  const defaultStart = useMemo(
    () =>
      deepLinkFrom && isValidDate(deepLinkFrom)
        ? dayjs(deepLinkFrom).startOf("day").toDate()
        : undefined,
    [deepLinkFrom],
  )
  // The window is on the JUDGEMENT time (when a run recorded the decision),
  // so it must reach today: the hook's default end is three days back (a
  // data-lag allowance for analytics pages), which hid every decision a run
  // made today. Three months back by default — a first run judges the whole
  // thread history in one go and should be visible without touching the picker.
  const { dateRange } = useMinMaxDateRange(
    undefined,
    maxDateRestriction,
    AggregationType.DAILY,
    false,
    undefined,
    "last3Months",
    defaultStart,
    defaultStart ? dayjs().startOf("day").toDate() : undefined,
  )
  const { setPeriod } = usePeriod()
  const from = dayjs(dateRange[0]).startOf("day").toISOString()
  const to = dayjs(dateRange[1]).endOf("day").toISOString()

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const window = { from, to, platform: platform || undefined }
  // The counts describe the whole window; one row is enough to get them.
  const { data: list } = useListCommentDecisionsQuery({ ...window, take: 1 })
  const { data: reactions, isLoading: reactionsLoading } =
    useGetAdReactionsQuery(window)
  const { data: summaries } = useListAdSummariesQuery()
  const { data: dashboard, isLoading: dashboardLoading } =
    useGetCommentDashboardQuery(window, { skip: !trendsOpen })
  const { data: cloud, isLoading: cloudLoading } = useGetCommentWordCloudQuery(
    window,
    { skip: !trendsOpen },
  )
  const counts = list?.counts

  return (
    <div className="flex flex-col gap-system-lg px-system-lg py-system-md text-white">
      <header className="flex flex-wrap items-end justify-between gap-system-md">
        <div>
          <h1 className="text-2xl font-semibold">Comment moderation</h1>
          <p className="text-sm text-system-gray-400">
            Comments on this brand&apos;s Meta ad posts and its own Facebook and
            Instagram feed, judged daily. Open an ad or post to read the room
            and to hide or unhide single comments.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-system-sm">
          <SegmentedToggle
            id="comment-moderation-platform"
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={value => setParam("platform", value)}
          />
          {isValidDate(dateRange[0]) && isValidDate(dateRange[1]) && (
            <DateRangePicker
              start={dateRange[0]}
              end={dateRange[1]}
              maxDate={new Date()}
              onChange={({ start, end }) =>
                void setPeriod(
                  dayjs(start).format(DATE_FORMAT),
                  dayjs(end).format(DATE_FORMAT),
                )
              }
            />
          )}
        </div>
      </header>

      <ModerationSettings />

      <section className="grid grid-cols-2 gap-system-sm md:grid-cols-4">
        <StatTile label="Judged" value={counts?.total ?? "–"} />
        <StatTile
          label="Hidden / flagged"
          value={counts?.hidden ?? "–"}
          hint="Hidden on Meta, or flagged while hiding is off (analyse only)"
        />
        <StatTile label="Kept visible" value={counts?.kept ?? "–"} />
        <StatTile
          label="Hide refused"
          value={counts?.hideFailed ?? "–"}
          hint="Meta refused the hide — usually no Page access (creator pages) or a missing permission"
        />
      </section>

      <AdsTable
        data={reactions}
        isLoading={reactionsLoading}
        summaries={summaries}
        from={from}
        to={to}
        platform={platform || undefined}
      />

      <section className="rounded-lg border border-basic-blue bg-basic-dark-blue">
        <div className="flex items-center justify-between px-4 py-2">
          <h2 className="text-base font-medium">Trends</h2>
          <Button
            size="xs"
            variant={{ variant: "text" }}
            onClick={() => setTrendsOpen(o => !o)}
          >
            {trendsOpen ? "Hide" : "Show"}
          </Button>
        </div>
        {trendsOpen && (
          <div className="flex flex-col gap-system-md border-t border-basic-blue px-4 py-3">
            <CommentDashboard data={dashboard} isLoading={dashboardLoading} />
            <div className="grid grid-cols-1 gap-system-md lg:grid-cols-2">
              <div>
                <h3 className="mb-2 text-base font-medium">
                  What people talk about
                </h3>
                <AspectRows aspects={reactions?.aspects ?? []} />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-base font-medium">
                    What people are saying
                  </h3>
                  <span className="text-xs text-system-gray-500">
                    <span className="text-rose-300">rose</span> = mostly hidden
                    · <span className="text-emerald-300">green</span> = mostly
                    kept
                  </span>
                </div>
                <CommentWordCloud terms={cloud} isLoading={cloudLoading} />
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
